/* Push subscription helper.
 *
 * Single-purpose hook + utilities for subscribing the current user to
 * Web Push (VAPID). We register a tiny, app-owned service worker at
 * `/sw.js` and store the subscription with the backend keyed by user
 * email. Topics: ["hot-leads", "warranty-claim-submitted"].
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) arr[i] = rawData.charCodeAt(i);
  return arr;
}

export const PUSH_SUPPORTED =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

async function ensureRegistration() {
  if (!PUSH_SUPPORTED) throw new Error("Push not supported in this browser");
  // Register the SW (idempotent — browsers cache by URL).
  let reg = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!reg) {
    reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  }
  await navigator.serviceWorker.ready;
  return reg;
}

/**
 * @param {{ topics?: string[] }} options
 */
export function usePush(options = {}) {
  const topics = options.topics || ["hot-leads", "warranty-claim-submitted"];
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Probe current subscription state on mount.
  useEffect(() => {
    let cancelled = false;
    if (!PUSH_SUPPORTED) return;
    (async () => {
      try {
        const reg = await ensureRegistration();
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(Boolean(sub));
      } catch (_) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Listen for SW messages (e.g. notification click → navigate).
  useEffect(() => {
    if (!PUSH_SUPPORTED) return;
    const onMsg = (event) => {
      const data = event.data || {};
      if (data.type === "OPEN_URL" && typeof data.url === "string") {
        try { window.location.href = data.url; } catch (_) {}
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, []);

  const subscribe = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      if (!PUSH_SUPPORTED) throw new Error("Push not supported");
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") throw new Error("Permission denied");

      const reg = await ensureRegistration();
      const { data: vk } = await api.get("/push/vapid-public-key");
      if (!vk?.public_key) throw new Error("Server has no VAPID key configured");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vk.public_key),
        });
      }
      await api.post("/push/subscribe", {
        subscription: sub.toJSON(),
        topics,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Subscribe failed";
      setError(msg);
      return false;
    } finally {
      setBusy(false);
    }
  }, [topics]);

  const unsubscribe = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const reg = await ensureRegistration();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); } catch (_) {}
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (e) {
      setError(e?.message || "Unsubscribe failed");
    } finally {
      setBusy(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    try {
      const r = await api.post("/push/test");
      return r.data;
    } catch (e) {
      return { error: e?.response?.data?.detail || e?.message };
    }
  }, []);

  return {
    supported: PUSH_SUPPORTED,
    permission,
    subscribed,
    busy,
    error,
    subscribe,
    unsubscribe,
    sendTest,
  };
}
